type MermaidApi = {
  initialize: (config: { startOnLoad: boolean }) => void;
  run: (options: { nodes: NodeListOf<HTMLElement> }) => Promise<void>;
};

type SudsPdfWindow = Window & {
  mermaid?: MermaidApi;
  __SUDS_PDF_MODE__?: boolean;
  __SUDS_RESOLVE_PDF_MERMAID__?: () => void;
  /** Puppeteer PDF fallback: convert leftover `.mermaid` SVGs to `<img>`. */
  __SUDS_REPLACE_MERMAID_CHARTS__?: () => Promise<void>;
};

/**
 * Replace a rendered Mermaid SVG with an `<img>` so paged.js / PDF print
 * lays the diagram out in normal document flow.
 *
 * @param chart - Element containing a rendered Mermaid SVG
 */
export async function replaceMermaidSvgWithImage(
  chart: Element,
): Promise<void> {
  const svg = chart.querySelector('svg');
  if (!svg) {
    return;
  }

  if (!svg.getAttribute('xmlns')) {
    svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  }

  const bounds = svg.getBoundingClientRect();
  const source = new XMLSerializer().serializeToString(svg);
  const dataUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
    source,
  )}`;

  const img = document.createElement('img');
  img.src = dataUrl;
  img.alt = 'mermaid diagram';
  img.className = 'graph graph-mermaid';
  img.style.maxWidth = '100%';
  img.style.height = 'auto';
  if (bounds.width > 0) {
    img.width = Math.ceil(bounds.width);
  }
  if (bounds.height > 0) {
    img.height = Math.ceil(bounds.height);
  }

  await new Promise<void>((resolve) => {
    img.onload = () => resolve();
    img.onerror = () => resolve();
  });
  chart.replaceWith(img);
}

/**
 * Convert every leftover `.mermaid` chart to a data-URL `<img>`.
 * Used in PDF mode after Mermaid runs, and by the CLI Puppeteer fallback.
 */
export async function replaceMermaidChartsWithImages(): Promise<void> {
  await Promise.all(
    Array.from(document.querySelectorAll('.mermaid')).map((chart) =>
      replaceMermaidSvgWithImage(chart),
    ),
  );
}

(window as SudsPdfWindow).__SUDS_REPLACE_MERMAID_CHARTS__ =
  replaceMermaidChartsWithImages;

/**
 * Draw Mermaid diagrams in server-rendered doc content.
 * That content is not hydrated, so the diagram component never mounts in the browser.
 *
 * In PDF mode, SVGs are swapped for images before paged.js paginates.
 */
export async function renderMermaidDiagrams(): Promise<void> {
  const win = window as SudsPdfWindow;
  const nodes = document.querySelectorAll<HTMLElement>('.mermaid');

  try {
    if (nodes.length && win.mermaid) {
      win.mermaid.initialize({ startOnLoad: false });
      try {
        await win.mermaid.run({ nodes });
      } catch (err: unknown) {
        console.log('mermaid render error: ', err);
      }

      if (win.__SUDS_PDF_MODE__) {
        await replaceMermaidChartsWithImages();
      }
    }
  } finally {
    win.__SUDS_RESOLVE_PDF_MERMAID__?.();
  }
}

/**
 * Starts Mermaid rendering and signals PDF prep completion on failure.
 */
export function setupMermaidDiagrams(): void {
  renderMermaidDiagrams().catch((err: unknown) => {
    console.log('mermaid setup error: ', err);
    (window as SudsPdfWindow).__SUDS_RESOLVE_PDF_MERMAID__?.();
  });
}
